import React from 'react';
import styled, { css } from 'styled-components';
import { select, withQuery } from '@panel/commons/core/themev2';
import { sendTrackingInfo } from '@panel/commons';
import PositiveIcon from '@panel/commons/assets/icons/positivo_normal.svg';
import FullIcon from '@panel/commons/assets/icons/full-icon.svg';
import NeutralIcon from '@panel/commons/assets/icons/neutro_normal.svg';
import NegativeIcon from '@panel/commons/assets/icons/negativo_normal.svg';
import PositiveIconHover from '@panel/commons/assets/icons/positivo_hover.svg';
import NeutralIconHover from '@panel/commons/assets/icons/neutro_hover.svg';
import NegativeIconHover from '@panel/commons/assets/icons/negativo_hover.svg';
import PublishArrow from '@panel/commons/assets/icons/publicar-aviso.svg';
import SimpleTooltip from '@panel/commons/lib/Tooltip/SimpleTooltip';

const siteId = process.env.REACT_APP_BUILD_SITE;
const isBrazilPortal = siteId && siteId.includes('BR');

const QualityArea = ({
  quality,
  qualityNotApply,
  device,
  isDuplicate,
  isPenalized,
  qualityStatus,
  usedForTour,
  validations,
  openModal,
  tooltipText,
  qualityText
}) => {
  const isFull = quality === 100 && !qualityNotApply;

  const tooltipComponent = text => {
    return <TooltipComponent>{text}</TooltipComponent>;
  };

  return (
    <ProgressContainer
      data-panel='posting-qualityInfo'
      qualityStatus={qualityStatus}
      isDuplicate={isDuplicate}
      isPenalized={isPenalized}
      data-tour-panel={usedForTour && 'quality'}
      hasTips={validations && validations.length > 0}
      completePercentage={quality}
      device={device}
      onClick={e => {
        // eslint-disable-next-line no-unused-expressions
        validations && validations.length > 0 && openModal(e, 'performanceTips');
        sendTrackingInfo('Panel', 'Mis avisos', 'Card del aviso-Completitud del aviso');
      }}
      hideImproveButton={!qualityStatus || qualityStatus === 'not_apply'}
      isFull={isFull}
    >
      <SimpleTooltip
        isMobile={device !== 'desktop' || !qualityStatus}
        text={isPenalized || qualityNotApply ? tooltipComponent(tooltipText) : tooltipText}
        dataPanel='detail-posting-qualityInfo'
      >
        <CircleChart
          hideImproveButton={!qualityStatus || qualityStatus === 'not_apply'}
          qualityStatus={qualityStatus}
          device={device}
          isDuplicate={isDuplicate}
          isPenalized={isPenalized}
          isFull={isFull}
          qualityNotApply={qualityNotApply}
        >
          <div className='single-chart'>
            <svg viewBox='0 0 36 36' className='circular-chart blue'>
              <path
                className='circle-bg'
                d='M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831'
              />
              <path
                className='circle'
                strokeDasharray={`${quality}, 100`}
                d='M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831'
              />
              <text x='18' y='20.35' className='percentage'>
                {quality}%
              </text>
              <text x='18' y='20.35' className='label'>
                {qualityText}
              </text>
            </svg>
          </div>
          <div className='icon-container'>
            <div className='face-icon' />
          </div>
        </CircleChart>
      </SimpleTooltip>
    </ProgressContainer>
  );
};

export default QualityArea;

const ProgressStyles = {
  ok: {
    icon: PositiveIcon,
    hoverIcon: PositiveIconHover,
    barColor: select('accessibleGreen')
  },
  warn: {
    icon: NeutralIcon,
    hoverIcon: NeutralIconHover,
    barColor: select('yellow')
  },
  error: {
    icon: NegativeIcon,
    hoverIcon: NegativeIconHover,
    barColor: select('negative')
  },
  not_apply: {
    icon: NeutralIcon,
    hoverIcon: NeutralIcon,
    barColor: select('darkGray')
  }
};

const ProgressContainer = styled.div`
  display: flex;
  align-items: center;
  cursor: ${({ hasTips }) => hasTips && 'pointer'};
  > div {
    &.PROGRESS-STATUS-ICON {
      background-image: ${({ qualityStatus, device, isFull }) => {
        if (isFull) return FullIcon;
        return (
          qualityStatus &&
          `url(${device !== 'tablet' ? ProgressStyles[qualityStatus].icon : ProgressStyles[qualityStatus].hoverIcon})`
        );
      }};
      min-width: ${({ isFull }) => (isFull ? 20 : 16)}px;
      min-height: 16px;
      vertical-align: sub;
      margin-right: 6.5px;
      background-size: cover;
    }
    &.POSTING-PROGRESS-BAR {
      width: 77px;
      ${withQuery.lg.down()} {
        width: 50px;
      }
      height: 10px;
      background-color: ${select('middleGray')};
      border-radius: 5px;
      margin-right: 8px;
      > div {
        height: 100%;
        border-radius: 5px;
        width: ${({ completePercentage }) => (completePercentage ? `${completePercentage}%` : '0%')};
        background-color: ${({ qualityStatus, device }) =>
          qualityStatus && device === 'tablet' ? ProgressStyles[qualityStatus].barColor : select('darkGray')};
      }
    }
    &.PERCENTAGE-INFO {
      font-size: 12px;
      font-style: normal;
      font-weight: 500;
      letter-spacing: 0em;
      text-align: left;
      color: ${select('darkGray')};
    }
    &.IMPROVE-AVISO {
      display: none;
      font-family: Hind;
      font-size: 12px;
      font-style: normal;
      font-weight: 500;
      line-height: 19px;
      letter-spacing: 0em;
      color: ${select('blue')};
      ::before {
        content: '';
        display: inline-block;
        background-image: ${`url(${PublishArrow})`};
        background-size: contain;
        width: 9px;
        height: 9px;
        margin-right: 6px;
        margin-left: 6px;
      }
    }
  }
  :hover {
    > div {
      &.IMPROVE-AVISO {
        display: ${({ isDuplicate, isPenalized, completePercentage, hideImproveButton }) =>
          !isDuplicate && !isPenalized && completePercentage < 100 && !hideImproveButton && 'inline-block'};
      }
      &.POSTING-PROGRESS-BAR {
        > div {
          background-color: ${({ qualityStatus, hideImproveButton }) =>
            qualityStatus && !hideImproveButton && ProgressStyles[qualityStatus].barColor};
        }
      }
      &.PROGRESS-STATUS-ICON {
        background-image: ${({ qualityStatus, hideImproveButton }) =>
          qualityStatus && !hideImproveButton && `url(${ProgressStyles[qualityStatus].hoverIcon})`};
      }
      &.PERCENTAGE-INFO {
        display: ${({ isDuplicate, isPenalized, completePercentage, hideImproveButton }) =>
          !hideImproveButton && !isDuplicate && !isPenalized && completePercentage < 100 && 'none'};
      }
    }
  }
  ${({ isFull }) =>
    isFull &&
    css`
      > div {
        &.IMPROVE-AVISO {
          display: ${({ isDuplicate, isPenalized, completePercentage, hideImproveButton }) =>
            !isDuplicate && !isPenalized && completePercentage < 100 && !hideImproveButton && 'inline-block'};
        }
        &.POSTING-PROGRESS-BAR {
          > div {
            background-color: ${({ qualityStatus, hideImproveButton }) =>
              qualityStatus && !hideImproveButton && ProgressStyles[qualityStatus].barColor};
          }
        }
        &.PROGRESS-STATUS-ICON {
          background-image: ${({ qualityStatus, hideImproveButton }) =>
            qualityStatus && !hideImproveButton && `url(${ProgressStyles[qualityStatus].hoverIcon})`};
        }
        &.PERCENTAGE-INFO {
          display: ${({ isDuplicate, isPenalized, completePercentage, hideImproveButton }) =>
            !hideImproveButton && !isDuplicate && !isPenalized && completePercentage < 100 && 'none'};
        }
      }
    `}
`;

const CircleChart = styled.div`
  display: flex;
  flex-flow: row nowrap;
  width: 62px;
  height: 62px;
  position: relative;

  .icon-container {
    background-color: white;
    right: 2px;
    bottom: 4px;
    position: absolute;
    width: fit-content;
    height: fit-content;
    border-radius: 50%;
    padding: 1px;
    .face-icon {
      background-image: ${({ qualityStatus, device, isFull }) => {
        if (isFull) return FullIcon;
        return (
          qualityStatus &&
          `url(${device === 'desktop' ? ProgressStyles[qualityStatus].icon : ProgressStyles[qualityStatus].hoverIcon})`
        );
      }};
      min-width: ${({ isFull }) => (isFull ? 20 : 16)}px;
      min-height: 16px;
      background-size: cover;
    }
  }

  .single-chart {
    width: 100%;
    justify-content: space-around;
  }

  .circular-chart {
    display: block;
    max-height: 250px;
  }

  .circle-bg {
    fill: none;
    stroke: #eee;
    stroke-width: 2.8;
  }

  .circle {
    fill: none;
    stroke-width: 2.8;
    stroke-linecap: round;
    animation: progress 1s ease-out forwards;
  }

  .circular-chart.blue .circle {
    stroke: ${({ hideImproveButton }) => (hideImproveButton ? '#eee' : select('grayMediumDark'))};
    display: ${({ isDuplicate, isPenalized }) => (isDuplicate || isPenalized ? 'none' : 'flex')};
  }

  .percentage {
    fill: ${select('darkGray')};
    font-size: 0.5em;
    text-anchor: middle;
    font-weight: 500;
    display: none;
  }

  .label {
    fill: ${select('darkGray')};
    font-size: ${({ qualityNotApply }) => (isBrazilPortal && qualityNotApply ? '0.36em' : '0.45em')};
    text-anchor: middle;
    font-weight: 400;
    display: flex;
  }

  :hover {
    .circular-chart.blue .circle {
      stroke: ${({ qualityStatus, hideImproveButton }) =>
        !hideImproveButton && qualityStatus ? ProgressStyles[qualityStatus].barColor : '#eee'};
    }
    .icon-container {
      .face-icon {
        background-image: ${({ qualityStatus, hideImproveButton, isDuplicate, isPenalized, isFull }) =>
          qualityStatus &&
          !hideImproveButton &&
          !isDuplicate &&
          !isPenalized &&
          `url(${isFull ? FullIcon : ProgressStyles[qualityStatus].hoverIcon})`};
      }
    }
    .percentage {
      display: flex;
      fill: ${({ qualityStatus, isDuplicate, isPenalized }) =>
        !isDuplicate && !isPenalized && qualityStatus && ProgressStyles[qualityStatus].barColor};
    }
    .label {
      display: none;
    }
  }

  ${({ device, qualityStatus }) =>
    device !== 'desktop' &&
    css`
      .circular-chart.blue .circle {
        stroke: ${({ hideImproveButton }) =>
          !hideImproveButton && qualityStatus ? ProgressStyles[qualityStatus].barColor : '#eee'};
      }
      .percentage {
        display: flex;
        fill: ${qualityStatus && ProgressStyles[qualityStatus].barColor} !important;
      }
      .label {
        display: none;
      }
    `}
  
  ${({ isDuplicate, isPenalized }) =>
    !isDuplicate &&
    !isPenalized &&
    css`
      .percentage {
        display: flex;
      }
      .label {
        display: none;
      }
    `}

  ${({ isFull, qualityStatus }) =>
    isFull &&
    css`
      .circular-chart.blue .circle {
        stroke: ${({ hideImproveButton }) =>
          !hideImproveButton && qualityStatus ? ProgressStyles[qualityStatus].barColor : '#eee'};
      }
      .icon-container {
        .face-icon {
          background-image: ${({ hideImproveButton, isDuplicate, isPenalized }) =>
            qualityStatus && !hideImproveButton && !isDuplicate && !isPenalized && `url(${FullIcon})`};
        }
      }
      .percentage {
        display: flex;
        fill: ${qualityStatus && ProgressStyles[qualityStatus].barColor} !important;
      }
      .label {
        display: none;
      }
    `}
  ${({ qualityNotApply }) =>
    qualityNotApply &&
    css`
      .percentage {
        display: none !important;
      }
      .circular-chart {
        cursor: not-allowed !important;
      }
      .label {
        display: flex !important;
      }
    `}

  ${({ isDuplicate, isPenalized, device }) => {
    return (isDuplicate || isPenalized) && device !== 'desktop'
      ? css`
          .percentage {
            display: none !important;
          }
          .label {
            display: flex !important;
          }
          .icon-container {
            .face-icon {
              background-image: url(${NeutralIcon}) !important;
            }
          }
        `
      : '';
  }}
`;

const TooltipComponent = styled.div`
  width: 200px;
  white-space: pre-wrap;
  padding: 3px 0;
`;
